<script setup>
import { ref, computed } from 'vue';
import { Card } from 'primevue';
import InputNumber from 'primevue/inputnumber';
import ANButton from '@/components/common/AnButton.vue';
import Chart from 'primevue/chart';
import { calculateFutureValue } from '@/utils/financeCalculators.js';

// Form data
const formData = ref({
  initialInvestment: 100000,
  annualContribution: 50000,
  returnRate: 12,
  durationYears: 10
});

const result = ref(null);
const chartData = ref({});
const chartOptions = ref({});

// Computed properties
const isValidInput = computed(() => {
  return (formData.value.initialInvestment >= 0 || formData.value.annualContribution > 0) &&
         formData.value.durationYears > 0 && 
         formData.value.returnRate > 0;
});

// Calculate Future Value
const calculate = () => {
  if (!isValidInput.value) return;

  result.value = calculateFutureValue(formData.value);
  generateChart();
  // Scroll to results section after calculation
  setTimeout(() => {
    const resultsElement = document.querySelector('.result-card');
    if (resultsElement) {
      resultsElement.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start',
        inline: 'nearest' 
      });
    }
  }, 100);
};

// Generate chart data
const generateChart = () => {
  const years = [];
  const lumpSumValues = [];
  const annuityValues = [];
  const totalValues = [];
  
  const r = formData.value.returnRate / 100;

  for (let year = 0; year <= formData.value.durationYears; year++) {
    // Future value of lump sum
    const lumpSumFV = formData.value.initialInvestment * Math.pow(1 + r, year);
    
    // Future value of annuity
    const annuityFV = year > 0 ? 
      formData.value.annualContribution * (((Math.pow(1 + r, year) - 1) / r)) : 0;
    
    const totalFV = lumpSumFV + annuityFV;
    
    years.push(`Year ${year}`);
    lumpSumValues.push(Math.round(lumpSumFV));
    annuityValues.push(Math.round(annuityFV));
    totalValues.push(Math.round(totalFV));
  }

  chartData.value = {
    labels: years,
    datasets: [
      {
        label: 'Lump Sum Growth',
        data: lumpSumValues,
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 2,
        fill: false
      },
      {
        label: 'Annual Contributions Growth',
        data: annuityValues,
        backgroundColor: 'rgba(255, 99, 132, 0.6)',
        borderColor: 'rgba(255, 99, 132, 1)',
        borderWidth: 2,
        fill: false
      },
      {
        label: 'Total Future Value',
        data: totalValues,
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 3,
        fill: false
      }
    ]
  };

  chartOptions.value = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Future Value Growth Over Time'
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return '₹' + (value / 100000).toFixed(1) + 'L';
          }
        }
      }
    }
  };
};

// Calculate breakdown by year
const calculateYearlyBreakdown = () => {
  const breakdown = [];
  const r = formData.value.returnRate / 100;
  
  for (let year = 1; year <= Math.min(10, formData.value.durationYears); year++) {
    const lumpSumFV = formData.value.initialInvestment * Math.pow(1 + r, year);
    const annuityFV = formData.value.annualContribution * (((Math.pow(1 + r, year) - 1) / r));
    const totalFV = lumpSumFV + annuityFV;
    
    breakdown.push({
      year,
      lumpSumValue: Math.round(lumpSumFV),
      annuityValue: Math.round(annuityFV),
      totalValue: Math.round(totalFV),
      totalInvested: formData.value.initialInvestment + (formData.value.annualContribution * year),
      growth: Math.round(totalFV - (formData.value.initialInvestment + (formData.value.annualContribution * year)))
    });
  }
  
  return breakdown;
};

// Initialize
calculate();
</script>

<template>
  <div class="future-value-calculator">
    <div class="calculator-grid">
      <!-- Input Form -->
      <Card class="input-card">
        <template #title>
          <h3>Future Value Calculator Input</h3>
        </template>
        <template #content>
          <div class="form-fields">
            <!-- Initial Investment -->
            <div class="field">
              <label for="initial">Initial Lump Sum Investment (₹)</label>
              <InputNumber
                id="initial"
                v-model="formData.initialInvestment"
                :min="0"
                :max="100000000"
                :step="10000"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
                class="input-field"
              />
              <small class="help-text">One-time investment amount (can be 0)</small>
            </div>

            <!-- Annual Contribution -->
            <div class="field">
              <label for="annual">Annual Contribution (₹)</label>
              <InputNumber
                id="annual"
                v-model="formData.annualContribution"
                :min="0"
                :max="10000000"
                :step="5000"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
                class="input-field"
              />
              <small class="help-text">Yearly additional investment (can be 0)</small>
            </div>

            <!-- Duration -->
            <div class="field">
              <label for="duration">Investment Duration (Years)</label>
              <InputNumber
                id="duration"
                v-model="formData.durationYears"
                :min="1"
                :max="50"
                :step="1"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
                class="input-field"
              />
            </div>

            <!-- Rate -->
            <div class="field">
              <label for="rate">Expected Annual Return (%)</label>
              <InputNumber
                id="rate"
                v-model="formData.returnRate"
                :min="1"
                :max="30"
                :step="0.5"
                :minFractionDigits="1"
                :maxFractionDigits="2"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
                class="input-field"
              />
            </div>

            <!-- Calculate Button -->
            <ANButton
              label="Calculate"
              severity="primary"
              size="large"
              @click="calculate"
              :disabled="!isValidInput"
              class="calculate-btn"
            />
          </div>
        </template>
      </Card>

      <!-- Results -->
      <Card v-if="result" class="result-card">
        <template #title>
          <h3>Future Value Results</h3>
        </template>
        <template #content>
          <div class="results">
            <div class="result-item highlight">
              <div class="result-label">Total Future Value</div>
              <div class="result-value">₹{{ result.futureValue.toLocaleString('en-IN') }}</div>
            </div>

            <div class="breakdown-section">
              <h4>Value Breakdown</h4>
              <div class="breakdown-grid">
                <div class="breakdown-item">
                  <div class="breakdown-label">From Lump Sum</div>
                  <div class="breakdown-value">₹{{ result.lumpSumGrowth.toLocaleString('en-IN') }}</div>
                </div>
                <div class="breakdown-item">
                  <div class="breakdown-label">From Annual Contributions</div>
                  <div class="breakdown-value">₹{{ result.annuityGrowth.toLocaleString('en-IN') }}</div>
                </div>
              </div>
            </div>

            <div class="result-summary">
              <div class="summary-item">
                <span class="summary-label">Total Investment:</span>
                <span class="summary-value">
                  ₹{{ (formData.initialInvestment + (formData.annualContribution * formData.durationYears)).toLocaleString('en-IN') }}
                </span>
              </div>
              <div class="summary-item">
                <span class="summary-label">Total Growth:</span>
                <span class="summary-value gain">
                  ₹{{ (result.futureValue - (formData.initialInvestment + (formData.annualContribution * formData.durationYears))).toLocaleString('en-IN') }}
                </span>
              </div>
              <div class="summary-item">
                <span class="summary-label">Growth Multiple:</span>
                <span class="summary-value gain">
                  {{ (result.futureValue / (formData.initialInvestment + (formData.annualContribution * formData.durationYears))).toFixed(2) }}x
                </span>
              </div>
            </div>
          </div>
        </template>
      </Card>
    </div>

    <!-- Chart -->
    <Card v-if="result && chartData.labels" class="chart-card">
      <template #title>
        <h3>Future Value Growth Visualization</h3>
      </template>
      <template #content>
        <div class="chart-container">
          <Chart type="line" :data="chartData" :options="chartOptions" class="chart" />
        </div>
      </template>
    </Card>

    <!-- Yearly Breakdown Table -->
    <Card v-if="result" class="breakdown-card">
      <template #title>
        <h3>Year-by-Year Growth (First 10 Years)</h3>
      </template>
      <template #content>
        <div class="breakdown-table">
          <div class="table-header">
            <span>Year</span>
            <span>Lump Sum Growth</span>
            <span>Contribution Growth</span>
            <span>Total Value</span>
            <span>Total Invested</span>
            <span>Net Growth</span>
          </div>
          <div 
            v-for="item in calculateYearlyBreakdown()" 
            :key="item.year"
            class="table-row"
          >
            <span>{{ item.year }}</span>
            <span>₹{{ item.lumpSumValue.toLocaleString('en-IN') }}</span>
            <span>₹{{ item.annuityValue.toLocaleString('en-IN') }}</span>
            <span class="highlight-value">₹{{ item.totalValue.toLocaleString('en-IN') }}</span>
            <span>₹{{ item.totalInvested.toLocaleString('en-IN') }}</span>
            <span class="growth-value">₹{{ item.growth.toLocaleString('en-IN') }}</span>
          </div>
        </div>
      </template>
    </Card>

    <!-- Investment Tips -->
    <Card class="tips-card">
      <template #title>
        <h3>💡 Investment Tips</h3>
      </template>
      <template #content>
        <div class="tips-grid">
          <div class="tip-item">
            <div class="tip-icon">🚀</div>
            <div class="tip-content">
              <h4>Power of Compounding</h4>
              <p>Starting early and staying consistent amplifies your wealth through compound growth.</p>
            </div>
          </div>
          <div class="tip-item">
            <div class="tip-icon">📈</div>
            <div class="tip-content">
              <h4>Regular Contributions</h4>
              <p>Annual contributions can significantly boost your final corpus through systematic investing.</p>
            </div>
          </div>
          <div class="tip-item">
            <div class="tip-icon">⚖️</div>
            <div class="tip-content">
              <h4>Realistic Returns</h4>
              <p>Conservative estimates (8-12%) help set achievable expectations for long-term planning.</p>
            </div>
          </div>
          <div class="tip-item">
            <div class="tip-icon">🎯</div>
            <div class="tip-content">
              <h4>Goal-Based Planning</h4>
              <p>Align your investment timeline with specific financial goals for better outcomes.</p>
            </div>
          </div>
        </div>
      </template>
    </Card>
  </div>
</template>

<style scoped>
.future-value-calculator {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.calculator-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

.form-fields {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field label {
  font-weight: 600;
  color: var(--an-text-primary);
  font-size: 14px;
}

.help-text {
  color: var(--an-text-secondary);
  font-size: 12px;
  font-style: italic;
}

.input-field {
  width: 100%;
}

.calculate-btn {
  width: 100%;
  margin-top: 16px;
}

.results {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.result-item {
  text-align: center;
  padding: 20px;
  border-radius: 8px;
  background: var(--an-background-light);
}

.result-item.highlight {
  background: linear-gradient(135deg, var(--an-primary), var(--an-secondary));
  color: var(--an-white);
}

.result-label {
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
  opacity: 0.9;
}

.result-value {
  font-size: 2rem;
  font-weight: 700;
}

.breakdown-section h4 {
  margin: 0 0 16px 0;
  color: var(--an-text-primary);
  font-size: 16px;
}

.breakdown-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.breakdown-item {
  text-align: center;
  padding: 16px;
  border-radius: 8px;
  background: var(--an-background-light);
  border: 1px solid var(--an-gray3);
}

.breakdown-label {
  font-size: 12px;
  font-weight: 500;
  color: var(--an-text-secondary);
  margin-bottom: 4px;
}

.breakdown-value {
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--an-text-primary);
}

.result-summary {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid var(--an-gray3);
}

.summary-label {
  color: var(--an-text-secondary);
  font-weight: 500;
}

.summary-value {
  font-weight: 600;
  color: var(--an-text-primary);
}

.summary-value.gain {
  color: var(--an-success);
}

.chart-container {
  height: 400px;
  width: 100%;
}

.chart {
  height: 100% !important;
  width: 100% !important;
}

.breakdown-table {
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-x: auto;
  max-height: 400px;
  overflow-y: auto;
}

.table-header,
.table-row {
  display: grid;
  grid-template-columns: 60px 140px 140px 140px 140px 140px;
  gap: 16px;
  padding: 12px;
  align-items: center;
  min-width: 800px;
}

.table-header {
  font-weight: 700;
  background: var(--an-background-light);
  border-radius: 6px;
  color: var(--an-text-primary);
  position: sticky;
  top: 0;
  z-index: 10;
}

.table-row {
  border-bottom: 1px solid var(--an-gray3);
  font-size: 14px;
}

.table-row:hover {
  background: var(--an-background-light);
}

.highlight-value {
  font-weight: 700;
  color: var(--an-primary);
}

.growth-value {
  font-weight: 700;
  color: var(--an-success);
}

.tips-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
}

.tip-item {
  display: flex;
  gap: 16px;
  padding: 20px;
  border-radius: 8px;
  background: var(--an-background-light);
  border-left: 4px solid var(--an-primary);
}

.tip-icon {
  font-size: 2rem;
  flex-shrink: 0;
}

.tip-content h4 {
  margin: 0 0 8px 0;
  color: var(--an-text-primary);
  font-size: 16px;
}

.tip-content p {
  margin: 0;
  color: var(--an-text-secondary);
  line-height: 1.5;
  font-size: 14px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .calculator-grid {
    grid-template-columns: 1fr;
  }
  
  .breakdown-grid {
    grid-template-columns: 1fr;
  }
  
  .result-value {
    font-size: 1.5rem;
  }
  
  .chart-container {
    height: 300px;
  }
  
  .tips-grid {
    grid-template-columns: 1fr;
  }
  
  .table-header,
  .table-row {
    grid-template-columns: 50px 120px 120px 120px 120px 120px;
    gap: 8px;
    font-size: 12px;
  }
}
</style>
